import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ProjectPlanDraft } from '@models/projectPlanDraft.model';
import { TaskTree, TaskTreeEditPatch } from '@components/molecules/TaskTree';
import { applyPlanChanges, removedTaskNames, revisionChanges } from '@utils/projectPlanDraft.utils';
import { planErrorCopy } from '@utils/planErrorCopy';
import { moneyPostureForRole } from '@constants/roleHierarchy';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { ChatPanel } from './ChatPanel';
import { CommitBar } from './CommitBar';
import { useTranslation } from '@hooks/useTranslation';

interface PlanReviewProps {
  draft: ProjectPlanDraft;
  onEditTask: (tempId: string, patch: TaskTreeEditPatch) => void;
  onDeleteTask?: (tempId: string) => void;
  onSendMessage: (message: string) => void;
  onRetry: () => void;
  commitLabel: string;
  onCommit: () => void;
  onSaveAsTemplate: () => void;
  onCancel: () => void;
  isCommitting?: boolean;
  showAdjustEndDates?: boolean;
  showCommit?: boolean;
  adjustEndDates: boolean;
  onAdjustEndDatesChange: (value: boolean) => void;
}

/** Sprint 31, story-161. `1fr 360px` grid: summary/assumptions + animated `TaskTree` left, `ChatPanel` right, `CommitBar` bottom (a sibling of the grid in this fixed-height column, so never over the list). */
export const PlanReview: React.FC<PlanReviewProps> = ({
  draft,
  onEditTask,
  onDeleteTask,
  onSendMessage,
  onRetry,
  commitLabel,
  onCommit,
  onSaveAsTemplate,
  onCancel,
  isCommitting,
  showAdjustEndDates,
  showCommit,
  adjustEndDates,
  onAdjustEndDatesChange,
}) => {
  const t = useTranslation('aiPlan');
  const role = useAppSelector((state: RootState) => state.user.role);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showAllRemoved, setShowAllRemoved] = useState(false);
  // tempId -> name for every task ever seen in this session, so a removed task can still be named
  // in the change summary after it has left `draft.plan`. Written in an effect, so during the
  // render where a removal arrives it still holds the pre-removal names.
  const namesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    draft.plan.forEach((task) => namesRef.current.set(task.tempId, task.name));
  }, [draft.plan]);

  const isReady = draft.status === 'READY';
  // Rows derive from the current plan on every render: removed tasks are never rendered, and there
  // is no previous-plan snapshot to go stale (Sprint 36, story-187). Only a revision's diff is
  // highlighted; the initial plan renders with no change highlight (walkthrough fix, sprint 38).
  const changes = useMemo(() => (isReady ? revisionChanges(draft) : []), [draft, isReady]);
  const rows = useMemo(() => applyPlanChanges(draft.plan, draft.plan, changes), [draft.plan, changes]);
  const removedNames = removedTaskNames(changes, namesRef.current);
  const showBudget = moneyPostureForRole(role) === 'ALL';

  const isBusy = draft.status === 'GENERATING';
  const isFailed = draft.status === 'FAILED';
  const errorCopy = planErrorCopy(draft.errorCode);

  return (
    <div className="flex flex-col h-[calc(100vh-13rem)] min-h-[480px]" data-testid="ai-plan-review">
      {isFailed && (
        <div
          role="alert"
          data-testid="ai-plan-error-banner"
          className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-300">{t[errorCopy.titleKey]}</p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t[errorCopy.bodyKey]}</p>
          </div>
          <button
            type="button"
            onClick={onRetry}
            data-testid="ai-plan-retry"
            className={
              errorCopy.retryStyle === 'primary'
                ? 'px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white flex-shrink-0'
                : 'px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-700 dark:text-red-300 flex-shrink-0'
            }>
            {t.retry}
          </button>
        </div>
      )}

      {/* No step list during a revision: the chat panel's working indicator covers it (walkthrough fix, sprint 38). */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 flex-1 min-h-0">
        <div className="min-w-0 overflow-y-auto p-1 pr-2 pb-4" data-testid="ai-plan-card-list">
          {draft.summary && <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{draft.summary}</p>}
          {draft.assumptions?.length > 0 && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowAssumptions((v) => !v)}
                className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t.assumptionsTitle} {showAssumptions ? '▴' : '▾'}
              </button>
              {showAssumptions && (
                <ul className="mt-1 list-disc pl-4 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                  {draft.assumptions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {removedNames.length > 0 && (
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400" data-testid="ai-plan-removed-summary">
              {t.changeRemoved.replace(
                '{names}',
                showAllRemoved || removedNames.length <= 3
                  ? removedNames.join(', ')
                  : t.changeRemovedMore.replace('{names}', removedNames.slice(0, 3).join(', ')).replace('{n}', String(removedNames.length - 3)),
              )}
              {removedNames.length > 3 && !showAllRemoved && (
                <button type="button" onClick={() => setShowAllRemoved(true)} className="ml-1 underline">
                  +{removedNames.length - 3}
                </button>
              )}
            </p>
          )}
          <TaskTree
            rows={rows}
            editable={isReady}
            animated
            showBudget={showBudget}
            onEditTask={onEditTask}
            onDeleteTask={onDeleteTask}
          />
        </div>
        <div className="min-w-0 min-h-[200px] overflow-hidden border-t lg:border-t-0 lg:border-l border-gray-100 dark:border-gray-700 pt-4 lg:pt-0 lg:pl-4">
          <ChatPanel messages={draft.messages} onSend={onSendMessage} disabled={isBusy} />
        </div>
      </div>

      <CommitBar
        commitLabel={commitLabel}
        onCommit={onCommit}
        onSaveAsTemplate={onSaveAsTemplate}
        onCancel={onCancel}
        adjustEndDates={adjustEndDates}
        onAdjustEndDatesChange={onAdjustEndDatesChange}
        isCommitting={isCommitting}
        showAdjustEndDates={showAdjustEndDates}
        showCommit={showCommit}
      />
    </div>
  );
};

export default PlanReview;
